import type { MapDocument } from "../document/types.ts";
import { highlightFrames, layerHighlight, type HighlightSources, type LayerHighlight } from "./layer-highlight.ts";
import type { FrameMaskOf } from "./occlusion.ts";

/**
 * Highlight layer's view state (ADR-070's amendment of 2026-10-04, decisions 2 and 8): whether it is on, and
 * the active layer's highlight, worked out once per map document and active layer. Outside history: never
 * saved, compiled, undone or sent under live editing, and off after a reload. A stroke's cells join the
 * highlight when the stroke ends and the document changes. A host that can read its atlas binds the frames'
 * solid pixels (`bindMasks`), as it binds the stand-in's frames, and "covered" is then measured; until it does,
 * and in an editor with no atlas to read, it is an estimate.
 */

/**
 * What the state reads from its editor and how it reports a change.
 */
export interface LayerHighlightHost {
  readonly map: () => MapDocument;
  readonly activeLayerId: () => string | null;
  readonly sources: () => HighlightSources;
  readonly notify: () => void;
}

/**
 * The highlight toggle of one editor and its remembered result.
 */
export class LayerHighlightState {
  private shown = false;
  private maskOf: FrameMaskOf | null = null;
  private from: { readonly map: MapDocument; readonly layerId: string; readonly playersAbove: number; readonly result: LayerHighlight | null } | null = null;

  /**
   * @param host The editor.
   */
  public constructor(private readonly host: LayerHighlightHost) {}

  /**
   * Whether the highlight is on.
   *
   * @returns True while on.
   */
  public get on(): boolean {
    return this.shown;
  }

  /**
   * Turns the highlight on or off, notifying only on a change.
   *
   * @param on Whether on.
   */
  public set(on: boolean): void {
    if (on === this.shown) {
      return;
    }
    this.shown = on;
    if (!on) {
      this.from = null;
    }
    this.host.notify();
  }

  /**
   * Gives the rules the solid pixels of the atlas's frames, by frame id, which only the host can read; the
   * next highlight is measured from them. Binding again, or null, works the highlight out afresh.
   *
   * @param maskOf The lookup, or null for the estimate.
   */
  public bindMasks(maskOf: FrameMaskOf | null): void {
    this.maskOf = maskOf;
    this.from = null;
  }

  /**
   * The frame ids the highlights of the map in view may ask the bound lookup for (`highlightFrames`), for a
   * host that reads their masks ahead of the first highlight.
   *
   * @returns The frame ids.
   */
  public frames(): string[] {
    return highlightFrames(this.host.map(), this.host.sources());
  }

  /**
   * Turns the highlight on when off and off when on.
   */
  public toggle(): void {
    this.set(!this.shown);
  }

  /**
   * The active layer's highlight: the same object until the map's document, the active layer or the Players
   * band changes.
   *
   * @returns The highlight, or null while off, with no active layer or with one the map does not hold.
   */
  public current(): LayerHighlight | null {
    const layerId = this.host.activeLayerId();
    if (!this.shown || layerId === null) {
      return null;
    }
    const map = this.host.map();
    const sources = this.host.sources();
    const from = this.from;
    if (from !== null && from.map === map && from.layerId === layerId && from.playersAbove === sources.playersAbove) {
      return from.result;
    }
    const result = layerHighlight(map, layerId, this.maskOf === null ? sources : { ...sources, maskOf: this.maskOf });
    this.from = { map, layerId, playersAbove: sources.playersAbove, result };
    return result;
  }
}
